import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { sendEvent, useEventNotification } from '../../features/visual-analytics/hooks/events';
import { ModalPortal } from '../modal-portal/ModalPortal';
import { yaml } from '@codemirror/lang-yaml';
import CodeMirror from '@uiw/react-codemirror';
import { parse as yamlParse } from 'yaml';

import styles from './AutoForm.module.scss';
import { PickList } from './PickList';
import { TagInputField } from './TagInputField';
import { SearchInputField } from './SearchInputField';
import { getUserError } from '../miscUtils';
import Markdown from 'react-markdown';

export enum FORM_DATATYPES {
  STRING = 'string',
  MULTIPLECHOICE = 'multiplechoice',
  NUMBER = 'number',
  TAGS = 'tags',
  YAML = 'yaml',
  SEARCH = 'search',
  CHECKBOX = 'checkbox',
}

const MSGAutoForm = 'PromptAutoForm';

export interface TpAutoFormChoiceIcon {
  iconName: string;
  iconPrefix?: 'fas' | 'far';
  title?: string;
}

export interface TpAutoFormChoice {
  id: string;
  content: string;
  title?: string;
  iconName?: string;
  iconPrefix?: 'fas' | 'far';
  icons?: TpAutoFormChoiceIcon[];
}

export interface TpAutoFormFieldDef {
  id: string;
  name: string;
  dataType: FORM_DATATYPES;
  startContent: any;
  required?: boolean;
  inputValidation?: (content: any) => boolean;
  ValidationMessage?: string;
  inputWarning?: (content: any) => boolean;
  WarningMessage?: string;
  choices?: TpAutoFormChoice[];
  lineCount?: number; // for string
  disableTransparentBackground?: boolean;
}

export interface TpAutoFormActionButtonDef {
  id: string;
  content: string;
  contentOnSubmit?: string;
  disabledOnSubmit?: boolean;
  icon?: string;
  isWizard?: boolean;
  isSecondary?: boolean;
}

export interface TpAutoFormDef {
  title: string;
  description?: string;
  fields: TpAutoFormFieldDef[];
  actionButtons?: TpAutoFormActionButtonDef[];
  requiredAsterisk?: boolean;
  submit?: (data: { [fieldId: string]: any }) => Promise<void>;
}

export interface TpAutoFormData {
  autoFormDef: TpAutoFormDef;
  callBackOK?: any;
  callBackCancel?: any;
}

export function promptAutoForm(autoFormDef: TpAutoFormDef) {
  // Make sure all multiple choice fields have a valid initial choice
  for (const field of autoFormDef.fields) {
    if (field.dataType == FORM_DATATYPES.MULTIPLECHOICE) {
      if (!field.startContent) field.startContent = field.choices![0].id;
    }
  }
  return new Promise((resolve, reject) => {
    function callBackOK(data: any) {
      resolve(data);
    }

    function callBackCancel() {
      reject();
    }

    const promptData: TpAutoFormData = {
      autoFormDef,
      callBackOK,
      callBackCancel,
    };
    sendEvent(MSGAutoForm, promptData);
  });
}

function getFieldEditor(
  field: TpAutoFormFieldDef,
  formData: { [fieldId: string]: any },
  updateFieldContent: (fieldId: string, content: any) => void,
): any {
  if (field.dataType == FORM_DATATYPES.CHECKBOX) {
    return (
      <input
        type="checkbox"
        style={{ width: '20px', height: '20px' }}
        id={field.id}
        checked={formData[field.id]}
        onChange={(ev) => {
          updateFieldContent(field.id, ev.target.checked);
        }}
      />
    );
  }
  if (field.dataType == FORM_DATATYPES.TAGS) {
    return <TagInputField id={field.id} onChange={(tags) => updateFieldContent(field.id, tags)} />;
  }
  if (field.dataType == FORM_DATATYPES.YAML) {
    return (
      <div className={styles.yamlInputContainer}>
        <CodeMirror
          value={'# Enter Key: Value prorties below: \n# key1: value1\n# key2: value2'}
          height={`100px`}
          width="100%"
          extensions={[yaml()]}
          onChange={(newContent) => updateFieldContent(field.id, newContent)}
          theme={'dark'}
          editable={true}
        />
      </div>
    );
  }
  if (field.dataType == FORM_DATATYPES.SEARCH) {
    if (field.choices) {
      return (
        <SearchInputField
          id={field.id}
          onUpdate={(selectedOptionId: string) => updateFieldContent(field.id, selectedOptionId)}
          fieldOptions={field.choices}
        />
      );
    }
  }
  if (field.dataType == FORM_DATATYPES.STRING) {
    if (field.lineCount && field.lineCount > 1) {
      return (
        <textarea
          id={field.id}
          rows={field.lineCount!}
          value={formData[field.id]}
          onChange={(ev) => {
            updateFieldContent(field.id, ev.target.value);
          }}
        />
      );
    }
    return (
      <input
        id={field.id}
        value={formData[field.id]}
        onChange={(ev) => {
          updateFieldContent(field.id, ev.target.value);
        }}
      />
    );
  }

  if (field.dataType == FORM_DATATYPES.NUMBER) {
    let content = formData[field.id];
    if (content === null || content === undefined) content = '';

    return (
      <input
        id={field.id}
        value={content}
        onChange={(ev) => {
          updateFieldContent(field.id, ev.target.value);
        }}
      />
    );
  }

  if (field.dataType == FORM_DATATYPES.MULTIPLECHOICE) {
    return (
      <PickList
        choices={field.choices!}
        selected={formData[field.id]}
        setSelected={(newId: string) => updateFieldContent(field.id, newId)}
        disableTransparentBackground={field.disableTransparentBackground}
      />
    );
    // return (
    //     <select
    //         value={formData[field.id]}
    //         onChange={(ev) => { updateFieldContent(field.id, ev.target.value) }}
    //     >
    //         {field.choices!.map(choice => (
    //             <option key={choice.id} value={choice.id}>{choice.content}</option>
    //         ))}
    //     </select>
    // );
  }

  throw `Invalid field type: ${field.dataType}`;
}

function getValidatedFieldData(
  autoFormDef: TpAutoFormDef,
  field: TpAutoFormFieldDef,
  content: any,
): [content: any, error: string | null] {
  const validatedContent = null;
  const error: string | null = null;

  if (field.dataType == FORM_DATATYPES.YAML) {
    if (!content) {
      if (!field.required) return ['', null];
      else return [null, 'Missing required field'];
    }
    try {
      const parsedContent = yamlParse(content);
      if (typeof parsedContent !== 'object') {
        return [null, 'Invalid YAML'];
      }
      return [parsedContent, null];
    } catch (e) {
      return [null, 'Invalid YAML'];
    }
  }
  if (field.dataType == FORM_DATATYPES.CHECKBOX) {
    if (content === undefined) {
      if (!field.required) return ['', null];
      else return [null, 'Must be checked'];
    }
    return [content, null];
  }
  if (
    [FORM_DATATYPES.STRING, FORM_DATATYPES.TAGS, FORM_DATATYPES.SEARCH, FORM_DATATYPES.CHECKBOX].includes(
      field.dataType,
    )
  ) {
    if (!content) {
      if (!field.required) return ['', null];
      else return [null, 'Missing required field'];
    }
    if (field.ValidationMessage && field.inputValidation && !field.inputValidation(content)) {
      return [null, field.ValidationMessage];
    } else {
      return [content, null];
    }
  }

  if (field.dataType == FORM_DATATYPES.NUMBER) {
    if (content === null || content === undefined || content === '') {
      if (!field.required) return [null, null];
      else return [null, 'Missing required field'];
    }
    if (field.ValidationMessage && field.inputValidation && !field.inputValidation(content))
      return [null, field.ValidationMessage];
    if (isNaN(content) || isNaN(parseFloat(content))) return [null, 'Invalid number'];
    return [parseFloat(content), null];
  }

  if (field.dataType == FORM_DATATYPES.MULTIPLECHOICE) {
    if (!content) {
      if (!field.required) return [undefined, null];
      else return [undefined, 'Missing required field'];
    }
    return [content, null];
  }

  throw getUserError(`Invalid field type: ${field.dataType}`);
}

export interface AutoFormRef {
  validateFormData: () => any;
}

interface TpAutoFormProps {
  activeForm: TpAutoFormData;
}

export const AutoForm = forwardRef<AutoFormRef, TpAutoFormProps>((props: TpAutoFormProps, ref) => {
  const { activeForm } = props;
  const [formData, setFormData] = useState<{ [id: string]: any }>({});
  const [formErrors, setFormErrors] = useState<{ [id: string]: string }>({});
  const [formWarnings, setFormWarnings] = useState<{ [id: string]: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!activeForm) return null;

  useEffect(() => {
    const theFormData: any = {};
    const theFormWarnings: { [id: string]: string } = {};
    for (const field of activeForm.autoFormDef.fields) {
      theFormData[field.id] = formData[field.id] || field.startContent;
      if (field.inputWarning && field.WarningMessage && field.inputWarning(theFormData[field.id])) {
        theFormWarnings[field.id] = field.WarningMessage;
      }
    }
    setFormData(theFormData);
    setFormWarnings(theFormWarnings);
    setSubmitError(null);
    setIsSubmitting(false);
  }, [activeForm]);

  function updateFieldContent(fieldId: string, newContent: any) {
    setFormData({
      ...formData,
      [fieldId]: newContent,
    });
    const field = activeForm.autoFormDef.fields.find((item) => item.id === fieldId);
    const inputWarning = field?.inputWarning;
    if (inputWarning && field.WarningMessage) {
      setFormWarnings((current) => ({
        ...current,
        [fieldId]: inputWarning(newContent) ? field.WarningMessage! : '',
      }));
    }
  }
  useImperativeHandle(ref, () => ({
    validateFormData: () => validateFormData(),
  }));

  function validateFormData(): { [fieldId: string]: any } | null {
    const validatedFormData: { [fieldId: string]: any } = {};
    const theFormErrors: { [id: string]: string } = {};
    let validated = true;
    for (const field of activeForm!.autoFormDef.fields) {
      const [validatedContent, fieldError] = getValidatedFieldData(activeForm!.autoFormDef, field, formData[field.id]);
      validatedFormData[field.id] = validatedContent;
      if (fieldError) {
        validated = false;
        theFormErrors[field.id] = fieldError;
      }
    }
    setFormErrors(theFormErrors);
    if (!validated) return null;
    return validatedFormData;
  }

  const fieldRows = activeForm.autoFormDef.fields.map((field) => (
    <tr key={field.id}>
      <td className={styles.fieldLabel}>
        <span dangerouslySetInnerHTML={{ __html: field.name }} />:
        {field.required && activeForm.autoFormDef.requiredAsterisk && <span className={styles.requiredMarker}>*</span>}
      </td>
      <td>{getFieldEditor(field, formData, updateFieldContent)}</td>
      {formErrors[field.id] && (
        <td>
          <div className={styles.validationError}>{formErrors[field.id]}</div>
        </td>
      )}
      {!formErrors[field.id] && formWarnings[field.id] && (
        <td>
          <div className={styles.validationWarning}>{formWarnings[field.id]}</div>
        </td>
      )}
    </tr>
  ));

  async function submitForm(validatedFormData: { [fieldId: string]: any }) {
    if (!activeForm.autoFormDef.submit) {
      activeForm.callBackOK(validatedFormData, true);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await activeForm.autoFormDef.submit(validatedFormData);
      activeForm.callBackOK(validatedFormData, true);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : String(error));
      setIsSubmitting(false);
    }
  }

  let buttons = [
    <button
      key="autoform-submit"
      onClick={() => {
        if (isSubmitting) return;
        const validatedFormData = validateFormData();
        if (validatedFormData) {
          validatedFormData.actionButtonId = 'ok';
          void submitForm(validatedFormData);
        }
      }}
      disabled={isSubmitting}
    >
      {isSubmitting ? 'Saving...' : 'OK'}
    </button>,
  ];

  if (activeForm.autoFormDef.actionButtons) {
    buttons = activeForm.autoFormDef.actionButtons.map((button) => (
      <React.Fragment key={button.id}>
        <div style={{ display: 'inline-block', width: '10px' }} />
        <button
          className={button.isSecondary ? styles.secondaryButton : undefined}
          disabled={isSubmitting && button.disabledOnSubmit}
          onClick={() => {
            if (isSubmitting) return;
            if (button.id === 'cancel') {
              activeForm.callBackCancel(true);
            } else if (button.isWizard) {
              const validatedFormData = validateFormData();
              if (validatedFormData) {
                validatedFormData.actionButtonId = button.id;
                activeForm.callBackOK(validatedFormData);
              }
            } else {
              const validatedFormData = validateFormData();
              if (validatedFormData) {
                validatedFormData.actionButtonId = button.id;
                void submitForm(validatedFormData);
              }
            }
          }}
        >
          {isSubmitting && button.contentOnSubmit ? button.contentOnSubmit : button.content}
        </button>
      </React.Fragment>
    ));
  }

  return (
    <div ref={ref as React.RefObject<HTMLDivElement>}>
      {Object.keys(formData).length > 0 && (
        <table>
          <tbody>{fieldRows}</tbody>
        </table>
      )}
      {activeForm.autoFormDef.requiredAsterisk && activeForm.autoFormDef.fields.some((field) => field.required) && (
        <p>
          <span className={styles.requiredNote}>
            <strong>*</strong>required
          </span>
        </p>
      )}
      {submitError && <div className={styles.validationError}>{submitError}</div>}
      <div className={styles.buttonSection}>{buttons}</div>
    </div>
  );
});

export function AutoFormModal() {
  const [activeForm, setActiveForm] = useState<TpAutoFormData | null>(null);

  useEventNotification(
    MSGAutoForm,
    (tpe: string, contentInput: any) => {
      const content = contentInput as TpAutoFormData;
      const initalCallbackOK = content.callBackOK;
      content.callBackOK = (data: any, close: boolean) => {
        initalCallbackOK(data);
        if (close) setActiveForm(null);
      };
      const initalCallbackCancel = content.callBackCancel;
      content.callBackCancel = (close: boolean) => {
        initalCallbackCancel();
        if (close) setActiveForm(null);
      };
      setActiveForm(content);
    },
    [],
  );

  if (!activeForm) return null;

  return (
    <ModalPortal
      // blurTarget={document.getElementById('dashboardClientArea')}
      close={() => {
        activeForm.callBackCancel(true);
      }}
    >
      <div className={styles.autoFormModal}>
        <div className={styles.title}>{activeForm.autoFormDef.title}</div>
        {activeForm.autoFormDef.description && (
          <div className={styles.description}>
            <Markdown>{activeForm.autoFormDef.description}</Markdown>
          </div>
        )}

        <AutoForm activeForm={activeForm} />
      </div>
    </ModalPortal>
  );
}
